import { getItem, getSect, getSkill } from '@jianghu/game-data';
import { countInBag } from '@jianghu/game-rules';
import { useEffect, useState } from 'react';
import { useGame } from '../../game/store';
import { Bar } from './Frames';
import { useGameInstance } from './GameContext';
import { Icon } from './Icon';

/** Re-renders every 100 ms while `active`; always returns the current time (never a stale value). */
function useNow(active: boolean): number {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 100);
    return () => window.clearInterval(id);
  }, [active]);
  return performance.now();
}

const ITEM_SLOTS = ['minor_healing_pill', 'minor_qi_pill'];

/** Technique hotbar (keys 1-4), consumables (5-6) and the experience bar. */
export function Hotbar() {
  const game = useGameInstance();
  const self = useGame((s) => s.self);
  const skills = useGame((s) => s.skills);
  const cooldowns = useGame((s) => s.cooldowns);
  const inventory = useGame((s) => s.inventory);
  const anyCd = Object.values(cooldowns).some((c) => c.end > performance.now());
  const now = useNow(anyCd);
  if (!self) return null;
  const sect = getSect(self.sect);
  const slots = sect?.skills ?? [];
  const pillCd = cooldowns['group:pill'];
  return (
    <div className="hotbar-wrap">
      <div className="hotbar" role="toolbar" aria-label="Techniques">
        {slots.map((id, i) => {
          const def = getSkill(id);
          const learned = skills.includes(id);
          const cd = cooldowns[id];
          const remaining = cd ? Math.max(0, cd.end - now) : 0;
          const frac = cd && remaining > 0 ? remaining / cd.total : 0;
          const noMana = def ? self.mp < def.manaCost : false;
          return (
            <button
              key={id}
              className={`slot ${learned ? '' : 'locked'} ${noMana ? 'nomana' : ''}`}
              title={
                def
                  ? `${def.name} — ${def.description}${def.manaCost ? ` (${def.manaCost} qi)` : ''}${learned ? '' : ` · Unlocks at level ${def.levelRequired}`}`
                  : ''
              }
              aria-label={def?.name}
              onPointerDown={(e) => {
                e.preventDefault();
                game?.triggerAction(`skill${i}` as never);
              }}
            >
              <span className="key">{i + 1}</span>
              <Icon name={def?.icon ?? 'empty'} />
              {frac > 0 && (
                <div
                  className="cd"
                  style={{
                    background: `conic-gradient(rgba(0,0,0,0.72) ${frac * 360}deg, rgba(0,0,0,0.15) 0)`,
                  }}
                >
                  {remaining > 1000 ? Math.ceil(remaining / 1000) : (remaining / 1000).toFixed(1)}
                </div>
              )}
              {!learned && <span className="count">Lv{def?.levelRequired}</span>}
            </button>
          );
        })}
        <div className="slot-sep" />
        {ITEM_SLOTS.map((defId, i) => {
          const def = getItem(defId);
          const n = countInBag(inventory as never, defId);
          const remaining = pillCd ? Math.max(0, pillCd.end - now) : 0;
          const frac = pillCd && remaining > 0 ? remaining / pillCd.total : 0;
          return (
            <button
              key={defId}
              className={`slot ${n === 0 ? 'locked' : ''}`}
              title={def ? `${def.name} — ${def.description}` : ''}
              aria-label={def?.name}
              onPointerDown={(e) => {
                e.preventDefault();
                game?.useHotbarItem(i);
              }}
            >
              <span className="key">{i + 5}</span>
              <Icon name={def?.icon ?? 'empty'} />
              {frac > 0 && (
                <div
                  className="cd"
                  style={{
                    background: `conic-gradient(rgba(0,0,0,0.72) ${frac * 360}deg, rgba(0,0,0,0.15) 0)`,
                  }}
                >
                  {Math.ceil(remaining / 1000)}
                </div>
              )}
              <span className="count">{n}</span>
            </button>
          );
        })}
      </div>
      <div className="xpbar">
        <Bar value={self.xp} max={self.xpNext} kind="xp" thin label="" />
      </div>
      <div className="muted pe-none" style={{ fontSize: 10, textShadow: '0 1px 1px #000', marginTop: -4 }}>
        XP {self.xp} / {self.xpNext}
      </div>
    </div>
  );
}
